import { fetchJSON as fetchAPI } from './client';

export type KnowledgeDashboard = {
  tenant_id: string;
  total_nodes: number;
  total_relationships: number;
  total_documents: number;
  total_memories: number;
  embeddings_indexed: number;
  searches_24h: number;
  context_packages_24h: number;
  avg_retrieval_ms: number;
  quality: QualityMetrics;
  unread_notifications: number;
  recent_activity?: ActivityEntry[];
  generated_at: string;
};

export type QualityMetrics = {
  tenant_id: string;
  freshness: number;
  confidence: number;
  completeness: number;
  consistency: number;
  coverage: number;
  conflicts: number;
  by_source?: Record<string, number>;
  generated_at: string;
};

export type KnowledgeNode = {
  id: string;
  tenant_id: string;
  type: string;
  name: string;
  description?: string;
  source?: string;
  confidence: number;
  version: number;
  brand_id?: string;
  created_at: string;
  updated_at: string;
};

export type KnowledgeRelationship = {
  id: string;
  from_node_id: string;
  to_node_id: string;
  type: string;
  weight?: number;
  confidence: number;
};

export type SearchResult = {
  id: string;
  node_id?: string;
  title: string;
  snippet: string;
  type?: string;
  score: number;
  keyword_score?: number;
  semantic_score?: number;
  source?: string;
};

export type ContextPackage = {
  id: string;
  tenant_id: string;
  request_id?: string;
  nodes?: KnowledgeNode[];
  relationships?: KnowledgeRelationship[];
  score: number;
  token_estimate: number;
  explanation?: string;
  rules_applied?: string[];
  generated_at: string;
};

export type MemoryObject = {
  id: string;
  kind: string;
  content: string;
  confidence: number;
  outcome?: string;
  reviewed: boolean;
  created_at: string;
};

export type KnowledgeRecommendation = {
  id: string;
  type: string;
  title: string;
  description: string;
  priority?: string;
};

export type ActivityEntry = {
  id: string;
  category: string;
  action: string;
  message: string;
  created_at: string;
};

export type KnowledgeArticle = {
  id: string;
  tenant_id: string;
  title: string;
  content: string;
  category?: string;
  tags?: string[];
  status: string;
  version: number;
  rating?: number;
  created_at: string;
  updated_at: string;
};

export type GraphResponse = {
  tenant_id: string;
  nodes: KnowledgeNode[];
  relationships: KnowledgeRelationship[];
  node_count: number;
  edge_count: number;
  generated_at: string;
};

export type RAGQueryResponse = {
  id: string;
  query: string;
  answer: string;
  citations?: Citation[];
  confidence: number;
  grounded: boolean;
  generated_at: string;
};

export type Citation = {
  id: string;
  source_type: string;
  source_id: string;
  title: string;
  excerpt: string;
  score: number;
};

export type EmbedResponse = {
  embedding_id: string;
  chunks: number;
  dimensions: number;
  model: string;
  indexed_at: string;
};

const API_BASE = import.meta.env.VITE_KNOWLEDGE_API ?? 'http://localhost:8113';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${API_BASE}/api/v1/knowledge${path}`, init);
}

export const knowledgeApi = {
  dashboard: () => fetchJSON<KnowledgeDashboard>('/dashboard'),
  sources: () => fetchJSON<string[]>('/sources'),
  nodeTypes: () => fetchJSON<string[]>('/node-types'),

  listNodes: () => fetchJSON<KnowledgeNode[]>('/nodes'),
  ingestNode: (body: { type: string; name: string; description?: string; source?: string }) =>
    fetchJSON<KnowledgeNode>('/nodes', { method: 'POST', body: JSON.stringify(body) }),
  exploreEntity: (id: string) => fetchJSON<Record<string, unknown>>(`/nodes/${id}/explore`),

  listRelationships: () => fetchJSON<KnowledgeRelationship[]>('/relationships'),

  search: (body: { query: string; mode?: string; limit?: number }) =>
    fetchJSON<SearchResult[]>('/search', { method: 'POST', body: JSON.stringify(body) }),

  buildContext: (body: { query: string; request_id?: string; brand_id?: string }) =>
    fetchJSON<ContextPackage>('/context', { method: 'POST', body: JSON.stringify(body) }),

  listMemories: (kind?: string) =>
    fetchJSON<MemoryObject[]>(`/memory${kind ? `?kind=${kind}` : ''}`),

  storeMemory: (body: { kind: string; content: string; outcome?: string }) =>
    fetchJSON<MemoryObject>('/memory', { method: 'POST', body: JSON.stringify(body) }),

  quality: () => fetchJSON<QualityMetrics>('/quality'),
  recommendations: () => fetchJSON<KnowledgeRecommendation[]>('/recommendations'),
  listActivity: (limit = 15) => fetchJSON<ActivityEntry[]>(`/activity?limit=${limit}`),
  adminHealth: () => fetchJSON<Record<string, unknown>>('/admin/health'),

  // DEV-000045
  listArticles: () => fetchJSON<KnowledgeArticle[]>('/articles'),
  createArticle: (body: { title: string; content: string; category?: string; tags?: string[] }) =>
    fetchJSON<KnowledgeArticle>('/articles', { method: 'POST', body: JSON.stringify(body) }),
  searchGET: (q: string, mode = 'hybrid', limit = 10) =>
    fetchJSON<SearchResult[]>(`/search?q=${encodeURIComponent(q)}&mode=${mode}&limit=${limit}`),
  embed: (content: string, articleId?: string) =>
    fetchJSON<EmbedResponse>('/embed', { method: 'POST', body: JSON.stringify({ content, article_id: articleId }) }),
  graph: () => fetchJSON<GraphResponse>('/graph'),
  ragQuery: (query: string) =>
    fetchJSON<RAGQueryResponse>('/query', { method: 'POST', body: JSON.stringify({ query }) }),
  citations: (queryId?: string) =>
    fetchJSON<Citation[]>(`/citations${queryId ? `?query_id=${queryId}` : ''}`),
};
